import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text, Switch, useToast, Heading } from "@chakra-ui/react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import useThemeColors from "../../../../../hooks/useThemeColors";
import SaveButtons from "./SaveButtons";
import { textSecondary } from "../../../../../utils/colors";
import { updateTenantSchedulingSettingsAction } from "../../../../../actions/tenantActions";
import { TENANT_UPDATE_SCHEDULING_SETTINGS_RESET } from "../../../../../constants/tenantConstants";
import { customSelectStyles } from "../../../../../components/input/customStyles";

const DEFAULT_OPENING_TIME = "08:00";
const DEFAULT_CLOSING_TIME = "17:00";

const WEEK_DAYS = [
  { value: "monday", labelKey: "clinicSettings.days.monday", fallback: "Monday" },
  { value: "tuesday", labelKey: "clinicSettings.days.tuesday", fallback: "Tuesday" },
  { value: "wednesday", labelKey: "clinicSettings.days.wednesday", fallback: "Wednesday" },
  { value: "thursday", labelKey: "clinicSettings.days.thursday", fallback: "Thursday" },
  { value: "friday", labelKey: "clinicSettings.days.friday", fallback: "Friday" },
  { value: "saturday", labelKey: "clinicSettings.days.saturday", fallback: "Saturday" },
  { value: "sunday", labelKey: "clinicSettings.days.sunday", fallback: "Sunday" },
];

const isWeekend = (day) => day === "saturday" || day === "sunday";

const buildWorkingHoursState = (workingHours = {}) => {
  const fallbackStart = workingHours.start || DEFAULT_OPENING_TIME;
  const fallbackEnd = workingHours.end || DEFAULT_CLOSING_TIME;
  const days = workingHours.days || {};

  return WEEK_DAYS.reduce((acc, { value }) => {
    const existing = days[value] || {};
    acc[value] = {
      enabled:
        existing.enabled !== undefined
          ? Boolean(existing.enabled)
          : !isWeekend(value),
      start: existing.start || fallbackStart,
      end: existing.end || fallbackEnd,
    };
    return acc;
  }, {});
};

const timeStringToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const match = value.match(/^(?:[01]\d|2[0-3]):[0-5]\d$/);
  if (!match) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
};

const minutesToTimeString = (minutes) => {
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) {
    return DEFAULT_OPENING_TIME;
  }
  const clamped = Math.min(Math.max(minutes, 0), 23 * 60 + 59);
  const hoursPart = Math.floor(clamped / 60)
    .toString()
    .padStart(2, "0");
  const minutesPart = (clamped % 60).toString().padStart(2, "0");
  return `${hoursPart}:${minutesPart}`;
};

const ClinicWorkingHours = ({ slug, tenant }) => {
  const { newBorderMode, backgroundLightMode } = useThemeColors();
  const toast = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [workingHoursByDay, setWorkingHoursByDay] = useState(() =>
    buildWorkingHoursState(tenant?.workingHours)
  );
  const [showButtons, setShowButtons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const baseWorkingHoursByDay = useMemo(
    () => buildWorkingHoursState(tenant?.workingHours),
    [tenant?.workingHours]
  );

  const baseWorkingHoursSerialized = useMemo(
    () => JSON.stringify(baseWorkingHoursByDay),
    [baseWorkingHoursByDay]
  );

  const workingHoursSerialized = useMemo(
    () => JSON.stringify(workingHoursByDay),
    [workingHoursByDay]
  );

  const tenantUpdateSchedulingSettings = useSelector(
    (state) => state.tenantUpdateSchedulingSettings
  );
  const { success, error } = tenantUpdateSchedulingSettings;

  const hourOptions = useMemo(() => {
    return Array.from({ length: 24 }, (_, index) => {
      const hour = index.toString().padStart(2, "0");
      const value = `${hour}:00`;
      return { value, label: value };
    });
  }, []);

  useEffect(() => {
    setShowButtons(workingHoursSerialized !== baseWorkingHoursSerialized);
  }, [workingHoursSerialized, baseWorkingHoursSerialized]);

  useEffect(() => {
    if (success && isSaving) {
      toast({
        title: t(
          "toasts.clinicSchedulingSettingsUpdate",
          "Scheduling settings updated"
        ),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setShowButtons(false);
    } else if (error && isSaving) {
      toast({
        title: t(
          "toasts.clinicSchedulingSettingsUpdateError",
          "Unable to update scheduling settings"
        ),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    if (success || error) {
      setIsSaving(false);
      dispatch({ type: TENANT_UPDATE_SCHEDULING_SETTINGS_RESET });
    }
  }, [success, error, dispatch, toast, t, isSaving]);

  useEffect(() => {
    setWorkingHoursByDay(buildWorkingHoursState(tenant?.workingHours));
  }, [tenant?.workingHours]);

  const handleDayToggle = (day, enabled) => {
    setWorkingHoursByDay((prev) => {
      const current = prev?.[day] || {
        enabled: !isWeekend(day),
        start: DEFAULT_OPENING_TIME,
        end: DEFAULT_CLOSING_TIME,
      };

      return {
        ...prev,
        [day]: {
          ...current,
          enabled,
        },
      };
    });
  };

  const handleDayTimeChange = (day, field, value) => {
    setWorkingHoursByDay((prev) => {
      const current = prev?.[day] || {
        enabled: !isWeekend(day),
        start: DEFAULT_OPENING_TIME,
        end: DEFAULT_CLOSING_TIME,
      };

      return {
        ...prev,
        [day]: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const handleSave = () => {
    let hasEnabledDay = false;
    let earliestMinutes = Infinity;
    let latestMinutes = -Infinity;
    const normalizedDays = {};

    for (const { value } of WEEK_DAYS) {
      const schedule = workingHoursByDay[value] || {
        enabled: !isWeekend(value),
        start: DEFAULT_OPENING_TIME,
        end: DEFAULT_CLOSING_TIME,
      };

      const startValue = schedule.start || DEFAULT_OPENING_TIME;
      const endValue = schedule.end || DEFAULT_CLOSING_TIME;
      const enabled = Boolean(schedule.enabled);

      const startMinutes = timeStringToMinutes(startValue);
      const endMinutes = timeStringToMinutes(endValue);

      if (startMinutes === null || endMinutes === null) {
        toast({
          title: t(
            "toasts.clinicSchedulingSettingsValidation",
            "Please complete all required fields"
          ),
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (enabled) {
        if (startMinutes >= endMinutes) {
          toast({
            title: t(
              "toasts.clinicSchedulingSettingsHoursInvalid",
              "Closing time must be later than opening time"
            ),
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        hasEnabledDay = true;
        earliestMinutes = Math.min(earliestMinutes, startMinutes);
        latestMinutes = Math.max(latestMinutes, endMinutes);
      }

      normalizedDays[value] = {
        enabled,
        start: startValue,
        end: endValue,
      };
    }

    if (!hasEnabledDay) {
      toast({
        title: t(
          "toasts.clinicSchedulingSettingsNoDaysEnabled",
          "Enable at least one working day"
        ),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (
      !Number.isFinite(earliestMinutes) ||
      !Number.isFinite(latestMinutes)
    ) {
      earliestMinutes = timeStringToMinutes(DEFAULT_OPENING_TIME) ?? 480;
      latestMinutes = timeStringToMinutes(DEFAULT_CLOSING_TIME) ?? 1020;
    }

    setIsSaving(true);
    dispatch(
      updateTenantSchedulingSettingsAction(slug, tenant.tenantID, {
        workingHours: {
          start: minutesToTimeString(earliestMinutes),
          end: minutesToTimeString(latestMinutes),
          days: normalizedDays,
        },
      })
    );
  };

  const handleCancel = () => {
    setWorkingHoursByDay(baseWorkingHoursByDay);
    setShowButtons(false);
  };

  return (
    <Box w="100%" p={["1.5rem 0", "3rem 0rem 3rem"]}>
      <Flex
        w="100%"
        direction="column"
        gap="1.25rem"
        bg={backgroundLightMode}
        borderRadius="1rem"
        p="1.25rem 1.5rem 1.25rem"
      >
        <Heading fontSize="1rem" fontWeight="600">
          {t("clinicSettings.dailyScheduleHeading", "Working hours by day")}
        </Heading>
        <Text color={textSecondary} fontSize="sm">
          {t(
            "clinicSettings.dailyScheduleDescription",
            "Choose which days the clinic operates and set the start and end times for each day."
          )}
        </Text>

        <Box
          border={`1px solid ${newBorderMode}`}
          borderRadius="0.75rem"
          overflow="hidden"
        >
          {WEEK_DAYS.map(({ value, labelKey, fallback }, index) => {
            const schedule = workingHoursByDay[value] || {
              enabled: !isWeekend(value),
              start: DEFAULT_OPENING_TIME,
              end: DEFAULT_CLOSING_TIME,
            };
            const isEnabled = Boolean(schedule.enabled);
            const startValue =
              hourOptions.find((option) => option.value === schedule.start) || null;
            const endValue =
              hourOptions.find((option) => option.value === schedule.end) || null;
            const label = t(labelKey, fallback);

            return (
              <Flex
                key={value}
                align="center"
                gap="1rem"
                flexWrap="wrap"
                px={["1rem", "1.25rem"]}
                py="0.85rem"
                borderTop={index === 0 ? "none" : `1px solid ${newBorderMode}`}
                opacity={isEnabled ? 1 : 0.6}
              >
                <Box minW="140px">
                  <Text fontWeight="600" fontSize="0.9rem">
                    {label}
                  </Text>
                  <Text fontSize="0.75rem" color={textSecondary}>
                    {isEnabled
                      ? t("clinicSettings.statusOpen", "Open")
                      : t("clinicSettings.statusClosed", "Closed")}
                  </Text>
                </Box>

                <Switch
                  isChecked={isEnabled}
                  onChange={(event) =>
                    handleDayToggle(value, event.target.checked)
                  }
                  colorScheme="purple"
                  aria-label={t(
                    "clinicSettings.toggleDay",
                    "Toggle working day"
                  )}
                />

                <Flex
                  gap="0.75rem"
                  flex="1"
                  minW={["100%", "260px"]}
                  align="center"
                  flexWrap="wrap"
                >
                  <Box minW={["100%", "120px"]} flex="1">
                    <Select
                      isSearchable={false}
                      options={hourOptions}
                      value={startValue}
                      onChange={(option) =>
                        handleDayTimeChange(
                          value,
                          "start",
                          option?.value || DEFAULT_OPENING_TIME
                        )
                      }
                      isDisabled={!isEnabled}
                      styles={customSelectStyles}
                      placeholder={t(
                        "form.selectOpeningTime",
                        "Opening time"
                      )}
                    />
                  </Box>
                  <Box minW={["100%", "120px"]} flex="1">
                    <Select
                      isSearchable={false}
                      options={hourOptions}
                      value={endValue}
                      onChange={(option) =>
                        handleDayTimeChange(
                          value,
                          "end",
                          option?.value || DEFAULT_CLOSING_TIME
                        )
                      }
                      isDisabled={!isEnabled}
                      styles={customSelectStyles}
                      placeholder={t(
                        "form.selectClosingTime",
                        "Closing time"
                      )}
                    />
                  </Box>
                </Flex>
              </Flex>
            );
          })}
        </Box>
      </Flex>

      {showButtons && (
        <SaveButtons onClose={handleCancel} onClick={handleSave} />
      )}
    </Box>
  );
};

export default ClinicWorkingHours;
